import type { UserWithToken } from 'hooks/useAuth';
import * as Icons from 'lucide-react';
import { isEdgeEnvironment } from '@/state/useIsEdgeEnvironment';
import * as Dashboard from './Screens/Dashboard';

// Namespace import, not named imports.
//
// Bun's dev bundler (1.3.11) wraps each module for HMR and rebuilds a synthetic
// namespace from the list of named bindings a module imports. For a barrel the
// size of lucide-react's entry (~3800 re-exports) that table is built wrong: a
// contiguous run of the requested names comes back undefined. Here bindings 5-7
// of 14 (Monitor, Sparkles, Users) were undefined, which rendered
// <undefined /> inside the sidebar links and took the whole authenticated app
// down with "Element type is invalid".
//
// Which names break shifts with the module graph, so this is not a lucide
// problem and cannot be worked around by reordering. Importing the namespace
// gives the bundler a real module object to hand over instead of a synthesised
// one. Keep this as a namespace import.
const {
  Globe,
  BarChart3,
  FlaskConical,
  Library,
  Layers,
  Lightbulb,
  Monitor,
  Sparkles,
  Users,
  Settings: SettingsIcon,
  User,
  Bot,
  Code,
  CreditCard,
  Mail,
  Flag,
  Megaphone,
  LogIn,
  Activity,
  HeartPulse,
  MessageSquare,
  TrendingUp,
  Beaker,
} = Icons;

(() => {
  const { hostname } = window.location;
  if (hostname === 'edge.pertento.ai') {
    document.title = 'Edge Pertento';
  } else if (hostname === 'localhost' || hostname === 'dev.pertento.ai') {
    document.title = 'Dev Pertento';
  }
})();

export type MenuLink = {
  key: string;
  label: string;
  title?: string;
  url?: string;
  icon: React.ComponentType<any> | string;
  element?: React.ReactNode;
  children?: MenuLink[];
  ignore?: boolean;
  hideInMenu?: boolean;
  /**
   * Marks a feature as beta. Declared once here and read by both surfaces that
   * show it — the sidebar entry and the page title — so the two cannot drift.
   */
  beta?: boolean;
};

function flattenMenuLinks(links: MenuLink[]): MenuLink[] {
  const result: MenuLink[] = [];
  for (const link of links) {
    if (link.ignore) continue;
    if (link.url && link.element) {
      result.push(link);
    }
    if (link.children) {
      result.push(...flattenMenuLinks(link.children));
    }
  }
  return result;
}

export function getFlatMenuLinks({ user }: { user: UserWithToken | null }): MenuLink[] {
  if (!user) return [];
  return flattenMenuLinks(getMenuLinks({ user }));
}

export const getMenuLinks = ({ user }: { user: UserWithToken }) => {
  const isAgency = user.company.type === 'Agency';
  const isSuperAdmin = user.role === 'Super Admin';
  const isEdge = isEdgeEnvironment();

  return [
    {
      key: 'dashboard',
      label: 'Dashboard',
      title: 'Dashboard',
      url: '/',
      icon: BarChart3,
      element: <Dashboard.HomeScreen />,
    },
    {
      key: 'clients',
      label: 'Clients',
      title: 'Clients',
      url: '/clients',
      icon: Users,
      element: <Dashboard.ClientsScreen />,
      ignore: !isAgency,
    },
    // Three ways of watching the same programme, so they sit together. Health
    // asks what is wrong and needs a person; Performance asks how the running
    // experiments are doing; Program asks what the concluded ones taught. All
    // three share the same scope tabs.
    {
      key: 'monitors',
      label: 'Monitors',
      title: 'Monitors',
      icon: Monitor,
      children: [
        {
          key: 'monitors.health',
          label: 'Health',
          title: 'Health',
          url: '/health',
          icon: HeartPulse,
          element: <Dashboard.HealthScreen />,
        },
        {
          key: 'monitors.performance',
          label: 'Performance',
          title: 'Performance',
          url: '/performance',
          icon: Activity,
          element: <Dashboard.PerformanceScreen />,
        },
        {
          key: 'monitors.program',
          label: 'Program',
          title: 'Program',
          url: '/program',
          icon: TrendingUp,
          element: <Dashboard.ProgramScreen />,
        },
      ],
    },
    {
      key: 'experiments',
      label: 'Experiments',
      title: 'Experiments',
      icon: FlaskConical,
      children: [
        {
          key: 'experiments.library',
          label: 'Library',
          title: 'Experiment Library',
          url: '/experiments/library',
          icon: Library,
          element: <Dashboard.ExperimentsLibrary />,
        },
        {
          key: 'experiments.learnings',
          label: 'Learnings',
          title: 'Experiment Learnings',
          url: '/experiments/learnings',
          icon: Lightbulb,
          element: <Dashboard.LearningsScreen />,
        },
        {
          key: 'experiments.pipelines',
          label: 'Pipelines',
          title: 'Experiment Pipelines',
          url: '/experiments/pipelines',
          icon: Layers,
          element: <Dashboard.PipelinesScreen />,
        },
        {
          key: 'experiments.combinationReport',
          label: 'Combination Report',
          title: 'Variant Combination Report',
          url: '/experiments/combination-report',
          icon: Activity,
          element: <Dashboard.CombinationReport />,
        },
        {
          key: 'experiments.deployed',
          title: 'Deployed Experiments',
          label: 'Deployed',
          url: '/experiments/deployed',
          icon: Monitor,
          element: <Dashboard.ExperimentsDeployed />,
          ignore: true,
        },
        {
          key: 'experiments.implementation',
          title: 'Implementation',
          label: 'Implementation',
          url: '/experiments/implementation',
          icon: Code,
          element: <Dashboard.ExperimentsImplementation />,
        },
        {
          key: 'experiments.details',
          title: 'Experiment Details',
          label: 'Details',
          url: '/experiments/:id',
          icon: FlaskConical,
          element: <Dashboard.ExperimentDetailsScreen />,
          hideInMenu: true,
        },
      ],
    },
    // Feature flags (P-PER-59): stored as experiments, but a feature of their own, so a section of
    // their own, laid out as Surveys is: a Library and a details page.
    {
      key: 'flags',
      label: 'Feature flags',
      title: 'Feature flags',
      icon: Flag,
      children: [
        {
          key: 'flags.library',
          label: 'Library',
          title: 'Feature flag Library',
          url: '/flags/library',
          // Its own icon, not the Library one: in the icon rail the headings are hairlines, so
          // three Library rows would look alike.
          icon: Flag,
          beta: true,
          element: <Dashboard.FlagsList />,
        },
        {
          key: 'flags.details',
          label: 'Feature flag',
          title: 'Feature flag',
          url: '/flags/:id',
          icon: Flag,
          beta: true,
          element: <Dashboard.FlagDetailsScreen />,
          hideInMenu: true,
        },
      ],
    },
    {
      key: 'surveys',
      label: 'Surveys',
      title: 'Surveys',
      icon: MessageSquare,
      children: [
        {
          key: 'surveys.library',
          label: 'Library',
          title: 'Survey Library',
          url: '/surveys/library',
          icon: Library,
          element: <Dashboard.SurveysLibrary />,
        },
        {
          key: 'surveys.new',
          label: 'New Survey',
          title: 'New Survey',
          url: '/surveys/new',
          icon: MessageSquare,
          element: <Dashboard.SurveyBuilder />,
          hideInMenu: true,
        },
        {
          // Kept for links and bookmarks that predate the details hub; it
          // resolves to the hub's Results tab rather than a screen of its own.
          key: 'surveys.results',
          label: 'Results',
          title: 'Survey Results',
          url: '/surveys/:id/results',
          icon: BarChart3,
          element: <Dashboard.SurveyResultsRedirect />,
          hideInMenu: true,
        },
        {
          key: 'surveys.edit',
          label: 'Edit Survey',
          title: 'Edit Survey',
          url: '/surveys/:id/edit',
          icon: MessageSquare,
          element: <Dashboard.SurveyBuilder />,
          hideInMenu: true,
        },
        {
          key: 'surveys.details',
          label: 'Details',
          title: 'Survey Details',
          url: '/surveys/:id',
          icon: MessageSquare,
          element: <Dashboard.SurveyDetailsScreen />,
          hideInMenu: true,
        },
      ],
    },
    {
      key: 'personalizations',
      label: 'Personalizations',
      title: 'Personalizations',
      icon: Sparkles,
      ignore: true,
      children: [
        {
          key: 'personalizations.library',
          label: 'Library',
          title: 'Personalizations Library',
          url: '/personalizations/library',
          icon: Library,
          element: <Dashboard.PersonalizationsLibrary />,
        },
      ],
    },
    {
      key: 'websites',
      label: 'Websites',
      title: 'Websites',
      icon: Globe,
      children: [
        {
          key: 'websites.list',
          label: 'Websites list',
          title: 'Websites',
          url: '/websites/list',
          icon: Globe,
          element: <Dashboard.WebsitesList />,
        },
        {
          key: 'websites.google-analytics',
          label: 'Google Analytics',
          title: 'Google Analytics',
          url: '/websites/google-analytics',
          icon: BarChart3,
          element: <Dashboard.GoogleAnalyticsScreen />,
        },
      ],
    },
    {
      key: 'settings',
      label: 'Settings',
      title: 'Settings',
      icon: SettingsIcon,
      children: [
        {
          key: 'settings.general',
          label: 'General',
          title: 'Settings · General',
          url: '/settings/general',
          icon: SettingsIcon,
          element: <Dashboard.SettingsGeneral />,
        },
        {
          key: 'settings.account',
          label: 'Account',
          title: 'Settings · Account',
          url: '/settings/account',
          icon: User,
          element: <Dashboard.AccountSettings />,
        },
        {
          key: 'settings.agencyTeam',
          label: 'Agency Team',
          title: 'Settings · Agency Team',
          url: '/settings/agency-team',
          icon: Users,
          element: <Dashboard.SettingsAgencyTeam />,
          ignore: !isAgency,
        },
        {
          key: 'settings.organizationTeam',
          label: 'Organization Team',
          title: 'Settings · Organization Team',
          url: '/settings/organization-team',
          icon: Users,
          element: <Dashboard.SettingsOrganizationTeam />,
        },
        {
          key: 'settings.billing',
          label: 'Billing',
          title: 'Settings · Billing',
          url: '/settings/billing',
          icon: CreditCard,
          element: <Dashboard.SettingsBilling />,
        },
        {
          key: 'settings.agentAccess',
          label: 'Agent Access',
          title: 'Settings · Agent Access',
          url: '/settings/agent-access',
          icon: Bot,
          element: <Dashboard.SettingsAgentAccess />,
        },
      ],
    },
    {
      key: 'admin',
      label: 'Admin Tools',
      title: 'Admin Tools',
      icon: SettingsIcon,
      hideInMenu: true,
      ignore: !isSuperAdmin,
      children: [
        {
          key: 'admin.emailer',
          label: 'Emailer',
          title: 'Admin · Emailer',
          url: '/admin/emailer',
          icon: Mail,
          element: <Dashboard.Emailer />,
          hideInMenu: true,
        },
        {
          key: 'admin.featureFlags',
          label: 'Branch Flags',
          title: 'Admin · Branch Flags',
          url: '/admin/feature-flags',
          icon: Flag,
          element: <Dashboard.FeatureFlags />,
          hideInMenu: true,
          ignore: !isEdge,
        },
        {
          key: 'admin.featureFlags.details',
          label: 'Branch Flag Details',
          title: 'Admin · Branch Flag',
          url: '/admin/feature-flags/:id',
          icon: Flag,
          element: <Dashboard.FeatureFlagDetails />,
          hideInMenu: true,
          ignore: !isEdge,
        },
        {
          key: 'admin.billing',
          label: 'Billing',
          title: 'Admin · Billing',
          url: '/admin/billing',
          icon: CreditCard,
          element: <Dashboard.AdminBilling />,
          hideInMenu: true,
        },
        {
          key: 'admin.betaFeatures',
          label: 'Beta Features',
          title: 'Admin · Beta Features',
          url: '/admin/beta-features',
          icon: Beaker,
          element: <Dashboard.AdminBetaFeatures />,
          hideInMenu: true,
        },
        {
          key: 'admin.announcement',
          label: 'Announcement',
          title: 'Admin · Announcement',
          url: '/admin/announcement',
          icon: Megaphone,
          element: <Dashboard.Announcement />,
          hideInMenu: true,
        },
        {
          key: 'admin.signinPanel',
          label: 'Signin Panel',
          title: 'Admin · Signin Panel',
          url: '/admin/signin-panel',
          icon: LogIn,
          element: <Dashboard.SigninPanel />,
          hideInMenu: true,
        },
        {
          key: 'admin.testExperiments',
          label: 'Test Experiments',
          title: 'Admin · Test Experiments',
          url: '/admin/test-experiments',
          icon: FlaskConical,
          element: <Dashboard.TestExperiments />,
          hideInMenu: true,
        },
        {
          key: 'admin.testExperiments.details',
          label: 'Test Experiment Details',
          title: 'Test Experiment Details',
          url: '/admin/test-experiments/:id',
          icon: FlaskConical,
          element: <Dashboard.ExperimentDetailsScreen />,
          hideInMenu: true,
        },
      ],
    },
  ];
};
